import type {
  ChartResult,
  GrahaBody,
  NakshatraPosition,
  Planet,
  VargaId,
} from '@astrally/shared';
import { BHAVA_NAMES, GRAHA_NAMES, JYOTISH_PLANETS, RASHI_NAMES } from '@astrally/shared';
import { formatDegMin, signLord } from '@astrally/astro-engine';
import { Term } from '../../components/Term';

/**
 * Granular per-graha breakdown — the reading heart of the kundli screen.
 *
 * One row per graha: Indian name, rashi, bhava (number + traditional name +
 * one-breath gist), nakshatra + pada, rashi/nakshatra lords, retrograde mark.
 * Every proper noun is a tappable <Term>, so a newcomer can open any word.
 *
 * For the D1 the rows come from the exact positions. For a varga (D9/D10) the
 * engine only re-maps SIGNS, so those rows show the varga rashi and a bhava
 * counted whole-sign from the varga lagna — the same counting the chart
 * drawings use — while degrees and nakshatras stay with the D1, and the note
 * above the list says so.
 *
 * No dignity (exalted/debilitated/own-sign) chip: the engine keeps its
 * exaltation tables private to the Varshaphal module and exposes no dignity
 * on chart positions, and computing our own here would fork the astronomy.
 */

/**
 * Glossary ids per sidereal sign index. Spelled out rather than derived from
 * RASHI_NAMES because the two spell Vrishchika differently (glossary
 * 'vrishchika', shared table 'Vrischika') — a derived id would silently fall
 * back to the generic rashi entry for exactly one sign.
 */
export const RASHI_TERM_IDS = [
  'rashi.mesha',
  'rashi.vrishabha',
  'rashi.mithuna',
  'rashi.karka',
  'rashi.simha',
  'rashi.kanya',
  'rashi.tula',
  'rashi.vrishchika',
  'rashi.dhanu',
  'rashi.makara',
  'rashi.kumbha',
  'rashi.meena',
] as const;

const isGraha = (p: Planet | 'lagna'): p is GrahaBody =>
  p !== 'lagna' && JYOTISH_PLANETS.includes(p);

/** 'graha.surya' etc. — the Sanskrit names double as glossary id tails. */
const grahaTermId = (b: GrahaBody) => `graha.${GRAHA_NAMES[b].toLowerCase()}`;

/** Indian name for the nine grahas; the raw id for anything unexpected. */
const grahaLabel = (p: Planet) => (isGraha(p) ? GRAHA_NAMES[p] : p);

interface GrahaRow {
  body: GrahaBody;
  /** Sidereal sign index 0-11 (varga-mapped for D9/D10). */
  sign: number;
  degInSign?: number;
  bhava?: number;
  nakshatra?: NakshatraPosition;
  retrograde: boolean;
}

export function GrahaDetail({ chart, varga }: { chart: ChartResult; varga: VargaId }) {
  const positions = chart.positions ?? [];
  let rows: GrahaRow[];
  let vargaLagnaSign: number | undefined;

  if (varga === 'D1') {
    rows = JYOTISH_PLANETS.flatMap((pl): GrahaRow[] => {
      const p = positions.find((x) => x.planet === pl);
      if (!p || !isGraha(p.planet)) return [];
      return [
        {
          body: p.planet,
          sign: p.sidereal.sign,
          degInSign: p.sidereal.degInSign,
          bhava: chart.jyotish?.bhavas?.[p.planet],
          nakshatra: p.nakshatra,
          retrograde: p.retrograde,
        },
      ];
    });
  } else {
    const vc = chart.jyotish?.vargas.find((v) => v.varga === varga);
    if (!vc) return null;
    vargaLagnaSign = vc.placements.find((p) => p.body === 'lagna')?.sign;
    // Retrogradation is a physical fact about the sky, not a varga mapping —
    // it carries over from the D1 positions unchanged.
    const retroOf = new Map(positions.map((p) => [p.planet, p.retrograde]));
    const lagnaSign = vargaLagnaSign;
    rows = JYOTISH_PLANETS.flatMap((pl): GrahaRow[] => {
      const p = vc.placements.find((x) => x.body === pl);
      if (!p || !isGraha(p.body)) return [];
      return [
        {
          body: p.body,
          sign: p.sign,
          bhava: lagnaSign === undefined ? undefined : ((p.sign - lagnaSign + 12) % 12) + 1,
          retrograde: retroOf.get(p.body) ?? false,
        },
      ];
    });
  }

  if (rows.length === 0) return null;

  return (
    <div className="card" style={{ padding: '4px 12px 6px' }}>
      {varga !== 'D1' && (
        <div className="tiny faint" style={{ padding: '8px 0 4px', lineHeight: 1.55 }}>
          {vargaLagnaSign !== undefined && (
            <>
              {varga} lagna:{' '}
              <Term id={RASHI_TERM_IDS[vargaLagnaSign]}>{RASHI_NAMES[vargaLagnaSign]}</Term>
              {' — '}
            </>
          )}
          a varga only re-maps each graha to a new rashi, so bhavas here are counted from the{' '}
          {varga} lagna, while degrees and nakshatras stay with the D1 birth kundli.
        </div>
      )}
      {rows.map((r, i) => (
        <div
          key={r.body}
          data-graha={r.body}
          style={{
            padding: '9px 0',
            borderTop: i > 0 || varga !== 'D1' ? '1px solid var(--border)' : undefined,
          }}
        >
          <div className="row" style={{ gap: 6, flexWrap: 'wrap', alignItems: 'baseline' }}>
            <b>
              <Term id={grahaTermId(r.body)}>{GRAHA_NAMES[r.body]}</Term>
            </b>
            {r.retrograde && (
              <span className="tiny gold">
                <Term id="retrograde">vakri ℞</Term>
              </span>
            )}
            <span className="small muted">in</span>
            <span>
              <Term id={RASHI_TERM_IDS[r.sign]}>{RASHI_NAMES[r.sign]}</Term>
            </span>
            {r.degInSign !== undefined && (
              <span className="small muted">{formatDegMin(r.degInSign)}</span>
            )}
          </div>
          <div className="tiny muted" style={{ marginTop: 3, lineHeight: 1.6 }}>
            {r.bhava !== undefined && (
              <>
                <Term id={`bhava.${r.bhava}`}>
                  Bhava {r.bhava} · {BHAVA_NAMES[r.bhava - 1].name}
                </Term>{' '}
                <span className="faint">({BHAVA_NAMES[r.bhava - 1].gist})</span>
              </>
            )}
            {r.nakshatra && (
              <>
                {r.bhava !== undefined && ' · '}
                <Term id={r.nakshatra.name}>{r.nakshatra.name}</Term> pada {r.nakshatra.pada}
              </>
            )}
          </div>
          <div className="tiny faint" style={{ marginTop: 2 }}>
            rashi lord {grahaLabel(signLord(r.sign))}
            {r.nakshatra && <> · nakshatra lord {grahaLabel(r.nakshatra.lord)}</>}
          </div>
        </div>
      ))}
    </div>
  );
}
